import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/auth/authService';
import { jsonAuthHeaders, AUTH_FETCH_INIT } from '../services/auth/apiAuth';
import { LinkEmailPassword } from '../components/auth/LinkEmailPassword';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { Card, Badge, Alert, Button } from '../components/ui';

// Use environment variable for API base URL with fallback for dev proxy
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Organization info from user profile endpoint (non-admin)
interface OrganizationInfo {
  id: string;
  name: string;
  subscription_tier: string;
}

export const ProfilePage: React.FC = () => {
  // Set document title
  useDocumentTitle('Profile');

  const { currentUser, systemRole, isAdmin, refreshIdToken } = useAuth();

  // Form state for name editing
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Organization state
  const [organization, setOrganization] = useState<OrganizationInfo | null>(
    null
  );
  const [orgLoading, setOrgLoading] = useState(false);

  // Initialize form with current user data
  useEffect(() => {
    if (currentUser) {
      setFirstName(currentUser.firstName || '');
      setLastName(currentUser.lastName || '');
    }
  }, [currentUser]);

  // Fetch user's organization using user-scoped profile endpoint
  useEffect(() => {
    const fetchOrganization = async () => {
      if (!currentUser?.uid) return;

      setOrgLoading(true);
      try {
        // Use the user-scoped /auth/profile endpoint instead of admin endpoints
        // This endpoint returns the user's profile including organization info
        // without requiring admin privileges
        // task_111: provider-aware headers. Firebase attaches a Bearer token;
        // Studio relies on the gml_sso cookie (credentials: 'include').
        const response = await fetch(`${API_BASE_URL}/auth/profile`, {
          ...AUTH_FETCH_INIT,
          headers: await jsonAuthHeaders(),
        });

        if (response.ok) {
          const profile = await response.json();
          if (profile.organization) {
            setOrganization(profile.organization);
          }
        } else {
          // Non-200 responses are expected for users without profiles
          console.log('Could not fetch profile:', response.status);
        }
      } catch (error) {
        // Silently fail - organization info is optional
        console.log('Could not fetch organization:', error);
      } finally {
        setOrgLoading(false);
      }
    };

    fetchOrganization();
  }, [currentUser?.uid]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await authService.updateProfile(
        firstName || undefined,
        lastName || undefined
      );
      setSaveSuccess(true);
      setIsEditing(false);

      // Refresh the token to get updated claims
      if (refreshIdToken) {
        await refreshIdToken();
      }

      // Reload to get fresh user data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Failed to update profile'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFirstName(currentUser?.firstName || '');
    setLastName(currentUser?.lastName || '');
    setIsEditing(false);
    setSaveError(null);
  };

  if (!currentUser) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Alert variant="error">Please sign in to view your profile.</Alert>
      </div>
    );
  }

  // Check if user has email/password linked
  // Note: This is a simplified check - in a real implementation you'd need to check Firebase User.providerData
  const hasEmailPassword = false; // Simplified for now

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-secondary-900 mb-6">
        Profile & Account Settings
      </h1>

      {saveSuccess && (
        <Alert variant="success" className="mb-4">
          Profile updated successfully!
        </Alert>
      )}

      {saveError && (
        <Alert variant="error" className="mb-4">
          {saveError}
        </Alert>
      )}

      {/* User Info Card */}
      <Card className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-lg font-semibold text-secondary-900">
            Account Information
          </h2>
          {!isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-secondary-500 mb-1 block">
                First Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter first name"
                />
              ) : (
                <p className="text-secondary-900">
                  {currentUser.firstName || 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm text-secondary-500 mb-1 block">
                Last Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter last name"
                />
              ) : (
                <p className="text-secondary-900">
                  {currentUser.lastName || 'Not set'}
                </p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-2 pt-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          )}

          <div>
            <p className="text-sm text-secondary-500 mb-1">Email Address</p>
            <p className="text-secondary-900">{currentUser.email}</p>
          </div>

          <div>
            <p className="text-sm text-secondary-500 mb-1">Display Name</p>
            <p className="text-secondary-900">
              {currentUser.displayName || 'Not set'}
            </p>
          </div>

          <div>
            <p className="text-sm text-secondary-500 mb-2">Role</p>
            <Badge variant={isAdmin ? 'primary' : 'default'}>
              {systemRole}
            </Badge>
          </div>

          <div>
            <p className="text-sm text-secondary-500 mb-2">Organization</p>
            {orgLoading ? (
              <p className="text-secondary-400 text-sm">Loading...</p>
            ) : organization ? (
              <div className="flex items-center gap-2">
                <span className="text-secondary-900">{organization.name}</span>
                <Badge variant="default">
                  {organization.subscription_tier}
                </Badge>
              </div>
            ) : (
              <p className="text-secondary-400 text-sm italic">
                Not assigned to an organization
              </p>
            )}
          </div>

          <div>
            <p className="text-sm text-secondary-500 mb-2">Sign-in Methods</p>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="success">Google</Badge>
              {hasEmailPassword && (
                <Badge variant="success">Email/Password</Badge>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Account Linking Section */}
      {!hasEmailPassword && (
        <>
          <hr className="my-6 border-secondary-200" />
          <h2 className="text-lg font-semibold text-secondary-900 mb-2">
            Add Sign-in Method
          </h2>
          <p className="text-sm text-secondary-600 mb-4">
            Link email/password sign-in to your account so you can sign in with
            either Google or email/password.
          </p>
          <LinkEmailPassword
            onSuccess={() => {
              // Could show a success message or refresh the page
              window.location.reload();
            }}
          />
        </>
      )}

      {hasEmailPassword && (
        <Alert variant="success" className="mt-6">
          <h3 className="font-semibold mb-1">Email/Password Already Linked</h3>
          <p className="text-sm">
            You can sign in using either Google or your email/password.
          </p>
        </Alert>
      )}
    </div>
  );
};
